// Durable client-side key/value storage for app state that must survive a tab
// being backgrounded and discarded.
//
// Mobile browsers wipe `sessionStorage` when they evict a backgrounded tab to
// reclaim memory — so state saved there is gone the moment the OS discards the
// tab (the "tab out and lose everything" case). `localStorage` survives that, a
// tab close, and a fresh tab. This module is the single backend the app's
// persistence hooks write through, so the choice + the SSR/private-mode/quota
// guards live in one place. When storage is unavailable (Safari private mode,
// SSR) it falls back to an in-memory map so callers never throw.

type KV = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;
const TOMBSTONE_PREFIX = "ln:persistence:tombstone:";
const COORDINATION_PREFIX = "ln:persistence:";
const tombstoneKey = (key: string) => `${TOMBSTONE_PREFIX}${encodeURIComponent(key)}`;

export interface ClientStorageDurability {
  mode: "durable" | "memory-only";
  reason: "unavailable" | "quota" | "oversize" | "invalid" | null;
  at: number | null;
}

export interface ClientStorageEnumeration {
  keys: string[];
  /** True only when the durable backend was enumerated without error. */
  complete: boolean;
}

let durability: ClientStorageDurability = {
  mode: "durable",
  reason: null,
  at: null,
};
const durabilityListeners = new Set<(status: ClientStorageDurability) => void>();
const durabilityFailures = new Map<
  string,
  { reason: Exclude<ClientStorageDurability["reason"], null>; at: number }
>();
const updateDurability = (next: ClientStorageDurability) => {
  if (durability.mode === next.mode && durability.reason === next.reason) return;
  durability = next;
  durabilityListeners.forEach((listener) => {
    try {
      listener({ ...next });
    } catch {
      // Observers cannot change storage semantics or starve later observers.
    }
  });
};

function createMemoryBackend(): KV {
  const map = new Map<string, string>();
  return {
    get length(): number {
      return map.size;
    },
    key(index: number): string | null {
      return Array.from(map.keys())[index] ?? null;
    },
    getItem(key: string): string | null {
      return map.has(key) ? (map.get(key) as string) : null;
    },
    setItem(key: string, value: string): void {
      map.set(key, value);
    },
    removeItem(key: string): void {
      map.delete(key);
    },
  };
}

let backendIsDurable = false;

function detectBackend(): KV {
  try {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
      backendIsDurable = false;
      updateDurability({ mode: "memory-only", reason: "unavailable", at: Date.now() });
      return createMemoryBackend();
    }
    // Safari private mode exposes localStorage but throws on write — probe it.
    const probe = "__ln_store_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    backendIsDurable = true;
    updateDurability({ mode: "durable", reason: null, at: null });
    return window.localStorage;
  } catch {
    backendIsDurable = false;
    updateDurability({ mode: "memory-only", reason: "unavailable", at: Date.now() });
    return createMemoryBackend();
  }
}

let backend: KV | null = null;
let memoryShadow: KV = createMemoryBackend();
const listeners = new Set<(key: string, value: string | null) => void>();
const notify = (key: string, value: string | null) =>
  listeners.forEach((listener) => {
    try {
      listener(key, value);
    } catch {
      // A UI observer is not part of the durable write transaction.
    }
  });
const envelopeTimestamp = (raw: string | null): number | null => {
  if (!raw) return null;
  try {
    const timestamp = JSON.parse(raw)?.timestamp;
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch { return null; }
};
function getBackend(): KV {
  if (backend === null) {
    backend = detectBackend();
    // Detection proves the backend itself works; it must not erase a
    // key-specific failure reported before the first durable read.
    refreshDurability();
  }
  return backend;
}

function refreshDurability(): void {
  if (!backendIsDurable) {
    updateDurability({ mode: "memory-only", reason: "unavailable", at: Date.now() });
    return;
  }
  const unresolved = Array.from(durabilityFailures.values()).pop();
  if (unresolved) {
    updateDurability({ mode: "memory-only", ...unresolved });
    return;
  }
  updateDurability({ mode: "durable", reason: null, at: null });
}

function recordDurabilityFailure(
  key: string,
  reason: Exclude<ClientStorageDurability["reason"], null>
): void {
  durabilityFailures.set(key, { reason, at: Date.now() });
  refreshDurability();
}

// Test seam: re-detect the backend (e.g. after the test env swaps localStorage).
// Not used by app code.
export function __resetClientStoreForTests(): void {
  backend = null;
  backendIsDurable = false;
  memoryShadow = createMemoryBackend();
  durabilityFailures.clear();
  durability = { mode: "durable", reason: null, at: null };
}

const ENUMERATION_FAILURE_KEY = "coordination:key-enumeration";

function matchingKeys(
  store: KV,
  predicate: (key: string) => boolean
): string[] {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key !== null && predicate(key)) keys.push(key);
  }
  return keys;
}

export const clientStore = {
  durability(): ClientStorageDurability {
    // Force backend detection so the first snapshot is honest in private mode.
    getBackend();
    return { ...durability };
  },
  reportMemoryOnly(
    reason: "quota" | "oversize" | "unavailable" | "invalid",
    key = `manual:${reason}`
  ): void {
    recordDurabilityFailure(key, reason);
  },
  clearDurabilityFailures(predicate: (key: string) => boolean): void {
    Array.from(durabilityFailures.keys())
      .filter(predicate)
      .forEach((key) => durabilityFailures.delete(key));
    refreshDurability();
  },
  subscribeDurability(listener: (status: ClientStorageDurability) => void): () => void {
    durabilityListeners.add(listener);
    listener(clientStore.durability());
    return () => durabilityListeners.delete(listener);
  },
  generation(key: string): number {
    try {
      const coordinationKey = tombstoneKey(key);
      const raw = memoryShadow.getItem(coordinationKey) ??
        getBackend().getItem(coordinationKey);
      if (raw === null) return 0;
      if (raw.length > 1024) {
        recordDurabilityFailure(coordinationKey, "invalid");
        return Number.NaN;
      }
      const parsed = JSON.parse(raw);
      if (
        !Number.isSafeInteger(parsed?.generation) || parsed.generation < 0 ||
        !Number.isFinite(parsed?.at) || parsed.at <= 0
      ) {
        recordDurabilityFailure(coordinationKey, "invalid");
        return Number.NaN;
      }
      durabilityFailures.delete(coordinationKey);
      refreshDurability();
      return parsed.generation;
    } catch {
      recordDurabilityFailure(tombstoneKey(key), "unavailable");
      return Number.NaN;
    }
  },
  tombstoneTimestamp(key: string): number {
    try {
      const coordinationKey = tombstoneKey(key);
      const raw = memoryShadow.getItem(coordinationKey) ??
        getBackend().getItem(coordinationKey);
      if (raw === null) return 0;
      if (raw.length > 1024) {
        recordDurabilityFailure(coordinationKey, "invalid");
        return Number.NaN;
      }
      const parsed = JSON.parse(raw);
      if (
        !Number.isSafeInteger(parsed?.generation) || parsed.generation < 0 ||
        !Number.isFinite(parsed?.at) || parsed.at <= 0
      ) {
        recordDurabilityFailure(coordinationKey, "invalid");
        return Number.NaN;
      }
      return parsed.at;
    } catch {
      recordDurabilityFailure(tombstoneKey(key), "unavailable");
      return Number.NaN;
    }
  },
  advanceTombstone(key: string): number {
    if (key.startsWith(COORDINATION_PREFIX)) return 0;
    const currentGeneration = clientStore.generation(key);
    const generation = Number.isSafeInteger(currentGeneration) &&
      currentGeneration >= 0
      ? currentGeneration + 1
      : Math.max(1, Date.now());
    const coordinationKey = tombstoneKey(key);
    const value = JSON.stringify({ generation, at: Date.now() });
    try {
      getBackend().setItem(coordinationKey, value);
      memoryShadow.removeItem(coordinationKey);
      durabilityFailures.delete(coordinationKey);
      refreshDurability();
    } catch {
      memoryShadow.setItem(coordinationKey, value);
      recordDurabilityFailure(coordinationKey, "quota");
    }
    return generation;
  },
  setItemGuarded(key: string, value: string, generation: number): boolean {
    if (clientStore.generation(key) !== generation) return false;
    return clientStore.setItem(key, value);
  },
  getItem(key: string): string | null {
    try {
      return memoryShadow.getItem(key) ?? getBackend().getItem(key);
    } catch {
      recordDurabilityFailure(key, "unavailable");
      return null;
    }
  },
  getDurableItem(key: string): string | null {
    try {
      const store = getBackend();
      return backendIsDurable ? store.getItem(key) : null;
    } catch {
      recordDurabilityFailure(key, "unavailable");
      return null;
    }
  },
  setItem(key: string, value: string): boolean {
    try {
      getBackend().setItem(key, value);
      memoryShadow.removeItem(key);
      durabilityFailures.delete(key);
      notify(key, value);
      if (backendIsDurable) {
        refreshDurability();
        return true;
      }
      recordDurabilityFailure(key, "unavailable");
      return false;
    } catch {
      // A backend can pass the startup probe and fail later (most commonly
      // quota pressure). Preserve the current session in memory.
      memoryShadow.setItem(key, value);
      recordDurabilityFailure(key, "quota");
      notify(key, value);
      return false;
    }
  },
  removeItem(key: string): boolean {
    const expectedGeneration = clientStore.advanceTombstone(key);
    let backendRemoved = true;
    try {
      getBackend().removeItem(key);
      durabilityFailures.delete(key);
    } catch {
      backendRemoved = false;
      recordDurabilityFailure(key, "unavailable");
    }
    memoryShadow.removeItem(key);
    refreshDurability();
    notify(key, null);
    let tombstoneDurable = key.startsWith(COORDINATION_PREFIX);
    if (!tombstoneDurable) {
      try {
        const raw = backendIsDurable
          ? getBackend().getItem(tombstoneKey(key))
          : null;
        const parsed = JSON.parse(raw ?? "null");
        tombstoneDurable = parsed?.generation === expectedGeneration;
      } catch {
        tombstoneDurable = false;
      }
    }
    return backendRemoved && clientStore.getItem(key) === null && tombstoneDurable;
  },
  /**
   * Enumerate stored keys and expose whether the durable backend was fully
   * inspected. Destructive callers must not interpret an enumeration error as
   * an empty store.
   */
  enumerate(prefix?: string): ClientStorageEnumeration {
    const predicate = (key: string) => (prefix ? key.startsWith(prefix) : true);
    let durableKeys: string[] = [];
    let shadowKeys: string[] = [];
    let complete = false;
    try {
      durableKeys = matchingKeys(getBackend(), predicate);
      complete = backendIsDurable;
    } catch {
      recordDurabilityFailure(ENUMERATION_FAILURE_KEY, "unavailable");
    }
    try {
      shadowKeys = matchingKeys(memoryShadow, predicate);
    } catch {
      recordDurabilityFailure(ENUMERATION_FAILURE_KEY, "unavailable");
      complete = false;
    }
    if (complete) {
      durabilityFailures.delete(ENUMERATION_FAILURE_KEY);
      refreshDurability();
    }
    return { keys: Array.from(new Set([...durableKeys, ...shadowKeys])), complete };
  },
  /** All known keys; use `enumerate()` when completeness affects correctness. */
  keys(prefix?: string): string[] {
    return clientStore.enumerate(prefix).keys;
  },
  /** Remove every key starting with `prefix`. */
  removeByPrefix(prefix: string): void {
    clientStore.keys(prefix)
      .filter((key) => !key.startsWith(COORDINATION_PREFIX))
      .forEach((key) => clientStore.removeItem(key));
  },
  /** Remove every key for which `predicate` returns true. */
  removeWhere(predicate: (key: string) => boolean): void {
    clientStore.keys()
      .filter((key) => !key.startsWith(COORDINATION_PREFIX) && predicate(key))
      .forEach((key) => clientStore.removeItem(key));
  },
  subscribe(listener: (key: string, value: string | null) => void): () => void {
    if (typeof window === "undefined") return () => undefined;
    const handler = (event: StorageEvent) => {
      if (event.storageArea === window.localStorage && event.key) {
        const shadow = memoryShadow.getItem(event.key);
        const incomingAt = envelopeTimestamp(event.newValue);
        const shadowAt = envelopeTimestamp(shadow);
        if (event.newValue === null || incomingAt === null || shadowAt === null || incomingAt >= shadowAt) {
          memoryShadow.removeItem(event.key);
          listener(event.key, event.newValue);
        }
      }
    };
    listeners.add(listener);
    window.addEventListener("storage", handler);
    return () => { listeners.delete(listener); window.removeEventListener("storage", handler); };
  },
};

// Send-intent identity must survive a reload BEFORE dispatch. Unlike ordinary
// UI state above, a memory fallback is not sufficient. Keep this strict access
// at the same storage boundary, and let callers stop before provider contact.
export const requiredDurableStore = {
  getItem(key: string): string | null {
    return window.localStorage.getItem(key);
  },
  setItem(key: string, value: string): void {
    window.localStorage.setItem(key, value);
    if (window.localStorage.getItem(key) !== value) {
      throw new Error("Could not persist the send record. Nothing was sent.");
    }
  },
  removeItem(key: string): void {
    window.localStorage.removeItem(key);
  },
};
